/**
 * OCP-930 - the operator bulk wallet credit (goodwill after an incident, a
 * refund wave) on the same rail as every money movement:
 *
 *   POST /v1/admin/wallet/bulk-credit/preview  -> recipients, unknown ids, total, limits + single-use token
 *   POST /v1/admin/wallet/bulk-credit          -> the same + confirmation_token
 *
 * The token binds the operator, the EXACT recipient set, the amount and the
 * reason, so any edit after the preview discards it. The hard limits are the
 * registry settings wallet.bulk_credit_max_recipients / _max_amount_cents /
 * _max_total_cents; the api checks them at preview and at execute.
 */
import { useState } from 'react';

import { api, ApiError } from '../lib/api';

interface BulkPreview {
  recipient_count: number;
  customer_ids: string[];
  unknown_customer_ids: string[];
  amount_cents: number;
  total_cents: number;
  limits: { max_recipients: number; max_amount_cents: number; max_total_cents: number };
  confirmation_token: string;
}

interface BulkResult {
  credited: Array<{ customer_id: string }>;
  failed: Array<{ customer_id: string; reason: string }>;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function eur(cents: number): string {
  return `${(cents / 100).toFixed(2)} EUR`;
}

export function BulkWalletCreditCard() {
  const [ids, setIds] = useState('');
  const [amountEur, setAmountEur] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customerIds = [...new Set((ids.match(UUID_RE) ?? []).map((x) => x.toLowerCase()))];
  const cents = Math.round(Number(amountEur) * 100);

  function edited(set: (v: string) => void) {
    return (v: string) => {
      set(v);
      setPreview(null);
      setResult(null);
    };
  }

  function body() {
    return { customer_ids: customerIds, amount_cents: cents, reason: reason.trim() };
  }

  function message(err: unknown): string {
    if (!(err instanceof ApiError)) return 'Failed.';
    const b = err.body as { error?: string; message?: string } | null;
    if (b?.error === 'bulk_limit_exceeded') return b.message ?? 'Above the bulk-credit limits.';
    if (b?.error === 'confirmation_used' || b?.error === 'confirmation_mismatch' || b?.error === 'confirmation_expired') return 'The preview is no longer valid - preview again.';
    return b?.message ?? `HTTP ${err.status}`;
  }

  async function runPreview() {
    if (busy) return;
    if (customerIds.length === 0) return setError('Paste at least one account id.');
    if (!Number.isInteger(cents) || cents <= 0) return setError('Enter a positive amount.');
    if (reason.trim().length < 10) return setError('A reason (at least 10 characters) is required - it is the ledger note and the audit reason.');
    setBusy(true);
    setError(null);
    try {
      setPreview(await api<BulkPreview>('/v1/admin/wallet/bulk-credit/preview', { method: 'POST', body: body() }));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (busy || !preview) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await api<BulkResult>('/v1/admin/wallet/bulk-credit', { method: 'POST', body: { ...body(), confirmation_token: preview.confirmation_token } }));
      setPreview(null);
    } catch (err) {
      setError(message(err));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  const input = 'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" data-testid="bulk-credit">
      <h2 className="text-sm font-semibold">Bulk wallet credit</h2>
      <p className="mt-1 text-xs text-gray-500">
        The same credit to several accounts (goodwill after an incident, a refund wave). Preview first; the confirmation is valid once, for
        10 minutes, for exactly these accounts, this amount and this reason.
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="block text-sm md:col-span-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Account ids (one per line or comma separated)</span>
          <textarea value={ids} onChange={(e) => edited(setIds)(e.target.value)} rows={3} aria-label="Account ids" className={`${input} font-mono text-xs`} />
          <span className="text-xs text-gray-500">{customerIds.length} account{customerIds.length === 1 ? '' : 's'}</span>
        </label>
        <label className="block text-sm">
          <span className="text-xs uppercase tracking-wide text-gray-500">Amount per account (EUR)</span>
          <input type="number" min="0" step="0.01" value={amountEur} onChange={(e) => edited(setAmountEur)(e.target.value)} aria-label="Amount per account" className={input} />
        </label>
        <label className="block text-sm">
          <span className="text-xs uppercase tracking-wide text-gray-500">Reason (ledger note + audit)</span>
          <input type="text" value={reason} onChange={(e) => edited(setReason)(e.target.value)} aria-label="Bulk credit reason" className={input} />
        </label>
      </div>
      {preview && (
        <div className="mt-3 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="bulk-credit-preview">
          {preview.recipient_count} account{preview.recipient_count === 1 ? '' : 's'} x {eur(preview.amount_cents)} = {eur(preview.total_cents)}
          {preview.unknown_customer_ids.length > 0 && (
            <div className="mt-1 text-red-700 dark:text-red-300">Not an account (will fail): {preview.unknown_customer_ids.join(', ')}</div>
          )}
          <div className="mt-1 text-amber-700 dark:text-amber-300">
            Limits: {preview.limits.max_recipients} accounts, {eur(preview.limits.max_amount_cents)} each, {eur(preview.limits.max_total_cents)} in total.
          </div>
        </div>
      )}
      {result && (
        <div className="mt-3 text-xs text-gray-700 dark:text-gray-300" data-testid="bulk-credit-result">
          Credited {result.credited.length} account{result.credited.length === 1 ? '' : 's'}
          {result.failed.length > 0 ? `; failed ${result.failed.map((f) => `${f.customer_id} (${f.reason})`).join(', ')}` : ''}.
        </div>
      )}
      {error && <div role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
      <div className="mt-3 flex justify-end">
        {preview ? (
          <button type="button" disabled={busy} onClick={() => void confirm()} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            {busy ? 'Crediting…' : 'Confirm bulk credit'}
          </button>
        ) : (
          <button type="button" disabled={busy} onClick={() => void runPreview()} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50">
            {busy ? 'Checking…' : 'Preview bulk credit'}
          </button>
        )}
      </div>
    </section>
  );
}
